import { studentInitialValue } from "const/constants";
import { resolveTermPaidAmountFromPayments } from "../Admissions/AddAdmissions/applicationFlow/termPaymentUtils";
import { format } from "date-fns";

const toLocalISODate = (d) => {
  const date = new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const isNavClientStub = (client) => Boolean(client?.id && !client?.name);

export const accountDataMatchesNavigation = (navItem, accountData) => {
  if (!accountData) return true;
  const targetAccountId = navItem?.clientAccountId;
  if (targetAccountId && accountData?.id) {
    return String(accountData.id) === String(targetAccountId);
  }
  const expectedClientId = navItem?.clientData?.id;
  if (!expectedClientId) return true;
  const payloadClientId = accountData.clientId ?? accountData.clientData?.id;
  if (!payloadClientId) return true;
  return String(payloadClientId) === String(expectedClientId);
};

export const resolveAccountClientData = (navClient, apiClient, prevClient) => {
  if (apiClient?.name) return apiClient;
  if (navClient?.name) return navClient;
  if (apiClient) return apiClient;
  if (prevClient?.name) return prevClient;
  return navClient ?? prevClient ?? null;
};

export const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

/** OSHC policies take a single commission payment; OVHC keeps the monthly ledger. Detected from
 * explicit type fields when present, otherwise from the linked-module summary the server builds. */
export const isOshcInsuranceRow = (row = {}) => {
  const haystack = [
    row.insuranceType,
    row.type,
    row.linkedModuleInsuranceType,
    row.linkedModuleSummary,
  ]
    .filter(Boolean)
    .map(String)
    .join(" ");
  if (/\bOVHC\b/i.test(haystack) && !/\bOSHC\b/i.test(haystack)) return false;
  return /\bOSHC\b/i.test(haystack);
};

/** Monthly insurance commission ledger summary for display. */
/** The server owns the monthly ledger and sends it oldest month first; a row with no ledger at all
 * still has its scalar commission, which is what a policy paid in one lump looks like. OSHC
 * one-time rows store month/forMonth as null — those still count when they have an amount. */
export const getInsuranceCommissionSummary = (row) => {
  const months = (row?.commissionMonths || []).filter((m) => toNumber(m.amount) > 0);
  if (months.length === 0) {
    const scalar = toNumber(row?.commission);
    return {
      count: scalar > 0 ? 1 : 0,
      total: scalar,
      lastMonth: null,
      lastReceivedDate: null,
    };
  }
  const latest = months[months.length - 1];
  return {
    count: months.length,
    total: months.reduce((sum, m) => sum + toNumber(m.amount), 0),
    lastMonth: latest?.month || latest?.forMonth || null,
    lastReceivedDate: latest?.receivedDate || null,
  };
};

export const isInstallmentCommissionClaimed = (installment = {}) =>
  (Boolean(installment.isCommissionInvoicePaid) && Boolean(installment.hasCommissionInvoice)) ||
  Boolean(installment.commissionClaimedDirect);

export const isInstallmentBonusClaimed = (installment = {}) =>
  (Boolean(installment.isBonusInvoicePaid) && Boolean(installment.hasBonusInvoice)) ||
  Boolean(installment.bonusClaimedDirect);

/**
 * A term's commission/bonus counts as "settled" for the completed view when it is either claimed
 * (provider invoice paid, direct/manual toggle) OR explicitly marked Not Applicable on its term.
 * A plain 0 that is neither claimed nor N/A is NOT settled — the value may still be set later.
 * `term` is the matching AdmissionTermResponse (carries the manual-toggle + N/A flags that the
 * imported installment DTO does not).
 */
export const isInstallmentCommissionSettled = (installment = {}, term = null) =>
  isInstallmentCommissionClaimed(installment) ||
  Boolean(term?.isCommissionClaimed) ||
  Boolean(term?.commissionNotApplicable);

export const isInstallmentBonusSettled = (installment = {}, term = null) =>
  isInstallmentBonusClaimed(installment) ||
  Boolean(term?.isBonusClaimed) ||
  Boolean(term?.bonusNotApplicable);

const buildTermLookupByNumber = (admissionTerms = []) => {
  const map = new Map();
  (admissionTerms || []).forEach((term) => {
    const num = Number(term?.termNumber);
    if (Number.isFinite(num)) map.set(num, term);
  });
  return map;
};

export const isCommissionMoneyClaimed = (row = {}) =>
  Boolean(row.commissionClaimedDirect) && Boolean(row.commissionClaimAffectsFinancials);
export const isBonusMoneyClaimed = (row = {}) =>
  Boolean(row.bonusClaimedDirect) && Boolean(row.bonusClaimAffectsFinancials);

/**
 * Claimed with its invoice kept outside this system ("chip only"). The server decides it. A label
 * only: the claim is priced like any other.
 */
export const isCommissionChipOnly = (row = {}) => Boolean(row.commissionChipOnly);
export const isBonusChipOnly = (row = {}) => Boolean(row.bonusChipOnly);

export const parseInstallmentTermNumber = (value, fallback = null) => {
  if (value == null || value === "") return fallback;
  const parsed = Number(String(value).trim().replace(/^T/i, ""));
  return Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * Term numbers an admission's installment table can show, sorted: every scheduled term, imported or
 * not, plus any installment the schedule no longer lists. An unlabelled installment takes its
 * position, as the table numbers it.
 */
export const admissionTermNumbersShown = (installments = [], terms = []) => {
  const fromInstallments = (installments || []).map((row, idx) =>
    parseInstallmentTermNumber(row?.terms, idx + 1)
  );
  const fromSchedule = (terms || []).map((term) => Number(term?.termNumber));
  return [...new Set([...fromInstallments, ...fromSchedule])]
    .filter((n) => Number.isInteger(n) && n > 0)
    .sort((a, b) => a - b);
};

export const getUnimportedAdmissionTermNumbers = (admissionDetails = [], admissionTerms = []) => {
  if (!Array.isArray(admissionTerms) || admissionTerms.length === 0) return [];
  const imported = new Set();
  (admissionDetails || []).forEach((d) => {
    const num = parseInstallmentTermNumber(d.terms, null);
    if (num != null) imported.add(num);
  });
  return admissionTerms.map((t) => t.termNumber).filter((num) => num != null && !imported.has(num));
};

export const getSwappableImportedTermNumbers = ({
  installmentRows = [],
  getVerificationForRow = () => null,
} = {}) =>
  (installmentRows || [])
    .filter((row) => !row._unimported && row.id != null)
    .filter((row) => getVerificationForRow(row)?.status === "IMPORTED")
    .map((row) => parseInstallmentTermNumber(row.terms, null))
    .filter((num) => num != null)
    .sort((a, b) => a - b);

export const countPendingVerificationsForAdmission = ({
  pendingPayments = [],
  pendingTerms = [],
  acountId,
  studentDbId,
}) => {
  const paymentCount = (pendingPayments || []).filter((p) =>
    acountId != null
      ? Number(p?.clientAccountDetailId) === Number(acountId)
      : Number(p?.admissionId) === Number(studentDbId)
  ).length;
  const termCount = (pendingTerms || []).filter(
    (v) =>
      (acountId != null && v?.clientAccountDetailId === acountId) ||
      Number(v?.submittedAccountDetail?.admissionValues?.studentDbId) === Number(studentDbId)
  ).length;
  return paymentCount + termCount;
};

export const admissionHasVerifiedPayment = ({
  admissionTerms = null,
  admissionDetails = [],
  hasVerifiedPayment = null,
} = {}) => {
  if (hasVerifiedPayment === true) return true;
  if (Array.isArray(admissionTerms) && admissionTerms.length > 0) {
    const fromTerms = admissionTerms.some((term) =>
      (term.payments || []).some((p) => p.status === "VERIFIED")
    );
    if (fromTerms) return true;
  }
  return (admissionDetails || []).some((detail) => toNumber(detail.paidAmount) > 0);
};

export const shouldShowAdmissionInAccounts = (row, context = {}) => {
  if (row?.module !== "Admission") return true;
  if (row.coeReceived !== false) return true;
  if (row.hasVerifiedPayment === true) return true;
  if (row.hasPendingVerification === true) return true;
  // A rejected payment still needs correcting, so the admission has to stay visible — hiding it
  // leaves no route back to the payment that was rejected.
  if (row.hasRejectedPayment === true) return true;
  if (
    admissionHasVerifiedPayment({
      admissionTerms: context.admissionTerms,
      admissionDetails: row.admissionDetails,
      hasVerifiedPayment: row.hasVerifiedPayment,
    })
  ) {
    return true;
  }
  const pendingCount =
    context.pendingVerificationCount ??
    countPendingVerificationsForAdmission({
      pendingPayments: context.pendingPayments,
      pendingTerms: context.pendingTerms,
      acountId: row.id,
      studentDbId: row.admissionValues?.studentDbId,
    });
  return pendingCount > 0;
};

export const shouldShowAllAdmissionTerms = (parentRow, context = {}) => {
  if (!parentRow || parentRow.module !== "Admission") return true;
  if (parentRow.coeReceived !== false) return true;
  return admissionHasVerifiedPayment({
    admissionTerms: context.admissionTerms,
    admissionDetails: parentRow.admissionDetails,
    hasVerifiedPayment: parentRow.hasVerifiedPayment,
  });
};

export const isAdmissionRowCompleted = (rowData, context = {}) => {
  if (rowData?.module !== "Admission") return false;
  const admDetails = rowData.admissionDetails || [];
  if (!admDetails.length) return false;

  const {
    admissionTerms = null,
    pendingVerificationCount = 0,
    admissionTermsLoaded = true,
  } = context;

  if (rowData.id && !admissionTermsLoaded) return false;
  if (pendingVerificationCount > 0) return false;
  if (getUnimportedAdmissionTermNumbers(admDetails, admissionTerms).length > 0) return false;

  const termLookup = buildTermLookupByNumber(admissionTerms);
  const parentDueCleared = Math.max(0, toNumber(rowData.dueAmount)) <= 0;
  const allImportedSettled = admDetails.every((d) => {
    const dueCleared = Math.max(0, toNumber(d.dueAmount)) <= 0;
    const term = termLookup.get(parseInstallmentTermNumber(d.terms, null));
    return (
      dueCleared && isInstallmentCommissionSettled(d, term) && isInstallmentBonusSettled(d, term)
    );
  });

  return parentDueCleared && allImportedSettled;
};

/**
 * Completion for any account row. Admission delegates to the term-aware check above; every other
 * module (Visa, RPL, Skill Assessment, Insurance, custom) is "completed" once it has a positive
 * agreed amount and nothing left due.
 */
export const isAccountRowCompleted = (rowData, context = {}) => {
  if (rowData?.module === "Admission") return isAdmissionRowCompleted(rowData, context);
  const agreed = toNumber(rowData?.agreedAmount) || toNumber(rowData?.agreed);
  if (agreed <= 0) return false;
  if (Math.max(0, toNumber(rowData?.dueAmount)) > 0) return false;
  // An insurance policy keeps earning after the client has finished paying: commission arrives
  // month by month, so the record is only finished once that ledger has been closed as well.
  if (rowData?.module === "Insurance") return Boolean(rowData?.commissionCompleted);
  return true;
};

export const createNewAccountDetail = () => ({
  uuid: crypto.randomUUID(),
  module: "",
  agreedAmount: 0,
  agreed: 0,
  cost: 0,
  dueAmount: 0,
  referral: 0,
  profitLoss: 0,
  paidAmount: 0,
  discount: 0,
  isCommissionClaimed: false,
  isBonusClaimed: false,
  commission: 0,
  bonus: 0,
  admissionValues: studentInitialValue,
  admissionDetails: [],
  notes: [],
});

export const createNewAdmissionDetail = (parentId, index) => ({
  parentId,
  index,
  uuid: crypto.randomUUID(),
  date: format(new Date(), "yyyy-MM-dd"),
  perSemFee: 0,
  perSemCost: 0,
  materialFee: 0,
  paidAmount: 0,
  dueAmount: 0,
  referral: 0,
  profitLoss: 0,
  commission: 0,
  bonus: 0,
  discount: 0,
  isCommissionClaimed: false,
  isBonusClaimed: false,
  notes: [], // Added to support notes in subtable
});

export const ADMISSION_TERM_SCHEDULE_REQUIRED_MESSAGE =
  "This admission has no term schedule. Add terms under Admissions → Offer letter received, then try again.";

/** Student.status once an application is withdrawn — set by StudentApplicationLifecycleService. */
export const ADMISSION_WITHDRAWN_STATUS = "Application Withdraw";

export const ADMISSION_MODULE_LOCKED_MESSAGE =
  "Cannot change or delete this Admission entry while installments are sent to a provider, invoiced, or claimed.";

export const ADMISSION_ALREADY_EXISTS_MESSAGE =
  "An account entry for this admission already exists for this client.";

export const ADMISSION_CREATE_FIRST_TOOLTIP =
  "Create an admission under Admissions → Add student first. The term schedule will appear in Accounts after verification.";

export const ADMISSION_CREATE_FIRST_TOAST =
  "No admission for this client yet. Create one under Admissions → Add student — the term schedule will appear in this account after verification.";

export const findDuplicateAdmissionRows = (accountDetails) => {
  const seen = new Map();
  const duplicates = [];
  (accountDetails || []).forEach((row, index) => {
    if (row.module !== "Admission") return;
    const studentDbId = row.admissionValues?.studentDbId;
    if (!studentDbId) return;
    if (seen.has(studentDbId)) {
      duplicates.push({ index, studentDbId, firstIndex: seen.get(studentDbId) });
    } else {
      seen.set(studentDbId, index);
    }
  });
  return duplicates;
};

export const admissionRowHasProtectedLinks = (row) => {
  if (!row || row.module !== "Admission") return false;
  return (row.admissionDetails || []).some(
    (installment) =>
      installment?.sentToProvider ||
      installment?.hasInvoice ||
      installment?.lockedByPaidProviderInvoice ||
      installment?.isCommissionClaimed ||
      installment?.isBonusClaimed
  );
};

/** Normalize API / Date values for HTML date inputs (yyyy-MM-dd). */
export const formatDateForInput = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string") {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return toLocalISODate(date);
  } catch {
    return "";
  }
};

export const isInstallmentScheduleLocked = (installment = {}) => {
  const status = String(installment.invoicePaymentStatus || "").toUpperCase();
  return (
    installment.lockedByPaidProviderInvoice === true ||
    installment.sentToProvider === true ||
    installment.hasInvoice === true ||
    status === "PAID" ||
    status === "PARTIALLY_PAID" ||
    toNumber(installment.paidAmount) > 0
  );
};

const findInstallmentForTerm = (existingDetails, term, index) => {
  if (!Array.isArray(existingDetails)) return null;
  if (term?.id) {
    const byId = existingDetails.find((d) => d.termId === term.id);
    if (byId) return byId;
  }
  const byIndex = existingDetails.find((d) => d.termIndex === index);
  if (byIndex) return byIndex;
  const termNumber = index + 1;
  const byTermNumber = existingDetails.find((d) => Number(d.terms) === termNumber);
  if (byTermNumber) return byTermNumber;
  const candidate = existingDetails[index];
  if (
    candidate &&
    candidate.termId == null &&
    candidate.termIndex == null &&
    !Number.isFinite(Number(candidate.terms))
  ) {
    return candidate;
  }
  return null;
};

export const scopePaymentVerificationsToCad = (payments = [], cadId) =>
  cadId == null
    ? []
    : (payments || []).filter((p) => Number(p?.clientAccountDetailId) === Number(cadId));

export const scopeTermVerificationsToEntry = (terms = [], { cadId, studentDbId } = {}) =>
  (terms || []).filter((v) => {
    if (cadId != null && v?.clientAccountDetailId === cadId) return true;
    const snapshotStudentDbId = v?.submittedAccountDetail?.admissionValues?.studentDbId;
    return studentDbId != null && Number(snapshotStudentDbId) === Number(studentDbId);
  });

/** Claimed money on reached terms: what the entry's profit already contains, as on the server. */
export const deriveParentClaimState = (installments = [], today = new Date()) => {
  const list = installments || [];
  const reached = list.filter((row) => isAdmissionTermReached(row, today));
  const withCommission = list.filter((row) => countedCommission(row) > 0);
  const withBonus = list.filter((row) => countedBonus(row) > 0);
  return {
    commission: reached.reduce(
      (sum, row) =>
        sum +
        (row.isCommissionInvoicePaid || row.isCommissionClaimed || isCommissionMoneyClaimed(row)
          ? countedCommission(row)
          : 0),
      0
    ),
    bonus: reached.reduce(
      (sum, row) =>
        sum +
        (row.isBonusInvoicePaid || row.isBonusClaimed || isBonusMoneyClaimed(row)
          ? countedBonus(row)
          : 0),
      0
    ),
    isCommissionClaimed:
      withCommission.length > 0 && withCommission.every((row) => row.isCommissionClaimed),
    isBonusClaimed: withBonus.length > 0 && withBonus.every((row) => row.isBonusClaimed),
  };
};

export const installmentsToTermSchedule = (installments = [], existingSchedule = []) => {
  const existingById = new Map((existingSchedule || []).filter((t) => t?.id).map((t) => [t.id, t]));
  return (installments || []).map((inst, index) => {
    const existing =
      (inst.termId && existingById.get(inst.termId)) || existingSchedule[index] || {};
    if (isInstallmentScheduleLocked(inst) && existing?.id) {
      return {
        id: existing.id,
        date: existing.date ?? "",
        censusDate: existing.censusDate ?? "",
        termFee: existing.termFee ?? "",
        materialFee: existing.materialFee ?? "",
        discount: existing.discount ?? "",
      };
    }
    return {
      id: inst.termId || existing.id || crypto.randomUUID(),
      date: inst.date ?? existing.date ?? "",
      censusDate: inst.censusDate ?? existing.censusDate ?? "",
      termFee: inst.perSemFee ?? existing.termFee ?? "",
      materialFee: inst.materialFee ?? existing.materialFee ?? "",
      discount: inst.discount ?? existing.discount ?? "",
    };
  });
};

export const getLockedTermIdsFromInstallments = (installments = []) => {
  const locked = new Set();
  (installments || []).forEach((inst) => {
    if (isInstallmentScheduleLocked(inst)) {
      if (inst.termId) {
        locked.add(inst.termId);
      }
      const termNumber = parseInt(
        String(inst.terms || "")
          .trim()
          .replace(/^[Tt]/, ""),
        10
      );
      if (!Number.isNaN(termNumber)) {
        locked.add(String(termNumber));
      }
    }
  });
  return locked;
};

/** Rebuild installment rows from the latest Admissions term schedule (preserves locked rows). */
export const reconcileAdmissionRowWithStudent = (
  row,
  { student, courseList = [], uniData = [] } = {}
) => {
  if (!row || row.module !== "Admission") return row;
  const schedule = getAdmissionTermSchedule({
    student,
    admissionValues: row.admissionValues,
  });
  if (!schedule.length) return row;

  const admissionValues = buildAdmissionValuesFromStudent({
    student,
    courseList,
    uniData,
    fallback: row.admissionValues,
  });
  const admissionDetails = buildAdmissionInstallmentsFromTermSchedule(
    row.uuid,
    schedule,
    row.admissionDetails || [],
    admissionValues,
    true
  );
  return calculateRowValues({
    ...row,
    admissionValues: {
      ...admissionValues,
      termSchedule: schedule,
    },
    admissionDetails,
  });
};

export const parseAdmissionApplicationData = (applicationData) => {
  if (!applicationData) return {};
  if (typeof applicationData === "object") return applicationData;
  try {
    return JSON.parse(applicationData);
  } catch {
    return {};
  }
};

export const getAdmissionTermSchedule = ({ student, admissionValues, applicationData } = {}) => {
  const fromStudent = student?.applicationData ?? applicationData;
  const appData = parseAdmissionApplicationData(fromStudent);
  const fromApplication = Array.isArray(appData?.termSchedule) ? appData.termSchedule : [];
  if (fromApplication.length > 0) return fromApplication;
  const local = admissionValues?.termSchedule;
  return Array.isArray(local) ? local : [];
};

export const admissionHasTermSchedule = (sources) => getAdmissionTermSchedule(sources).length > 0;

export const findAdmissionRowsWithoutTermSchedule = (accountDetails, studentList = []) =>
  (accountDetails || [])
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => {
      if (row.module !== "Admission") return false;
      if ((row.admissionDetails || []).length > 0) return false;
      const student = (studentList || []).find((s) => s?.id === row.admissionValues?.studentDbId);
      return !admissionHasTermSchedule({ student, admissionValues: row.admissionValues });
    });

export const validateAdmissionAccountDetails = (accountDetails, studentList = []) => {
  const invalid = findAdmissionRowsWithoutTermSchedule(accountDetails, studentList);
  if (invalid.length === 0) return { valid: true };
  return {
    valid: false,
    message: ADMISSION_TERM_SCHEDULE_REQUIRED_MESSAGE,
    rowIndex: invalid[0].index,
  };
};

const firstPresent = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "") ?? "";

const firstPresentOrNull = (...values) => {
  const value = values.find((item) => item !== undefined && item !== null && item !== "");
  return value ?? null;
};

export const buildAdmissionValuesFromStudent = ({
  student,
  courseList = [],
  uniData = [],
  fallback = studentInitialValue,
}) => {
  if (!student) return fallback;

  const appData = parseAdmissionApplicationData(student?.applicationData);
  const courseId = firstPresentOrNull(student?.course, appData?.course, fallback?.courseId);
  const universityId = firstPresentOrNull(
    student?.university,
    appData?.university,
    fallback?.universityId
  );
  const state = firstPresent(student?.state, appData?.state, fallback?.state);
  const intake = firstPresent(student?.intake, appData?.intake, fallback?.intake);

  const course = courseList?.find((item) => Number(item?.courseId) === Number(courseId));
  const offering =
    course?.offerings?.find((item) => Number(item?.universityId) === Number(universityId)) || {};
  const university = uniData?.find((item) => Number(item?.id) === Number(universityId)) || {
    id: offering?.universityId,
    name: offering?.universityName,
  };
  const onshoreFee = offering?.onshoreFees?.find((item) => item?.state === state) || {};

  const termSchedule = Array.isArray(appData?.termSchedule)
    ? appData.termSchedule
    : fallback?.termSchedule || [];
  const startDate = firstPresent(
    student?.courseStartDate ? toLocalISODate(student.courseStartDate) : "",
    appData?.courseStartDate,
    fallback?.startDate
  );
  const endDate = firstPresent(
    student?.courseEndDate ? toLocalISODate(student.courseEndDate) : "",
    appData?.courseEndDate,
    fallback?.endDate
  );
  const feePerSem = firstPresent(
    onshoreFee?.feePerSem,
    offering?.feePerSem,
    student?.fee,
    appData?.fee
  );
  const totalFees = firstPresent(onshoreFee?.totalFees, offering?.totalFees, appData?.totalFees);

  return {
    ...fallback,
    studentDbId: student?.id ?? fallback?.studentDbId ?? null,
    client: student?.name || fallback?.client || "",
    studentId: firstPresent(
      student?.studentId,
      appData?.studentId,
      student?.studentNumber,
      fallback?.studentId
    ),
    course: course?.courseName || fallback?.course || "",
    courseId,
    university: university?.name || offering?.universityName || fallback?.university || "",
    universityId: firstPresentOrNull(universityId, university?.id, fallback?.universityId),
    state,
    intake,
    startDate,
    endDate,
    totalFees: totalFees || fallback?.totalFees || "",
    agreedFees:
      appData?.agreedFeePerSemester ||
      onshoreFee?.feeAfterScholarship ||
      offering?.feeAfterScholarship ||
      fallback?.agreedFees ||
      "",
    discount:
      appData?.agreedDiscount ||
      onshoreFee?.discount ||
      offering?.discount ||
      student?.discountPercentage ||
      fallback?.discount ||
      "",
    materialFee: firstPresent(
      appData?.materialFee,
      offering?.materialFee,
      offering?.materialFees,
      student?.materialFee,
      fallback?.materialFee
    ),
    enrolmentFee: firstPresent(
      appData?.enrolmentFee,
      appData?.enrollmentFee,
      offering?.enrolmentFee,
      offering?.enrollmentFee,
      student?.enrollmentFee,
      fallback?.enrolmentFee
    ),
    semester: firstPresent(
      termSchedule?.length > 0 ? termSchedule.length : "",
      appData?.term,
      appData?.semester,
      offering?.noOfSem,
      fallback?.semester
    ),
    perSemFee: feePerSem || fallback?.perSemFee || "",
    agentName: appData?.agentName || fallback?.agentName || "",
    agreedFeePerUnit: appData?.agreedFeePerUnit ?? fallback?.agreedFeePerUnit ?? "",
    agreedDiscountPerUnit: appData?.agreedDiscountPerUnit ?? fallback?.agreedDiscountPerUnit ?? "",
    termSchedule,
  };
};

export const buildAdmissionInstallmentsFromTermSchedule = (
  parentId,
  termSchedule,
  existingDetails = [],
  context = {},
  onlyUpdateExisting = false
) => {
  if (!Array.isArray(termSchedule) || termSchedule.length === 0) return [];
  const feePerUnit = Number(context?.agreedFeePerUnit) || 0;
  const discountPerUnit = Number(context?.agreedDiscountPerUnit) || 0;
  const resolveFee = (term) =>
    term.termFeeManual
      ? Number(term.termFee) || 0
      : feePerUnit > 0
      ? Number(term.units || 0) * feePerUnit
      : Number(term.termFee) || 0;
  const resolveDiscount = (term) =>
    term.discountManual
      ? Number(term.discount) || 0
      : discountPerUnit > 0
      ? Number(term.units || 0) * discountPerUnit
      : Number(term.discount) || 0;

  return termSchedule
    .map((term, index) => {
      const existing = findInstallmentForTerm(existingDetails, term, index) || {};
      if (onlyUpdateExisting && !existing.id) return null;
      const locked = isInstallmentScheduleLocked(existing);
      const resolvedFee = resolveFee(term);
      const resolvedDiscount = resolveDiscount(term);
      return calculateSubRowValues(
        {
          ...createNewAdmissionDetail(parentId, index + 1),
          ...existing,
          parentId,
          index: index + 1,
          termIndex: index,
          termId: term.id || existing.termId,
          date: locked
            ? existing?.date || term.date || format(new Date(), "yyyy-MM-dd")
            : term.date || existing?.date || format(new Date(), "yyyy-MM-dd"),
          censusDate: locked
            ? existing?.censusDate ?? term.censusDate ?? ""
            : term.censusDate ?? existing?.censusDate ?? "",
          terms: String(index + 1),
          perSemFee: locked
            ? existing?.perSemFee ?? resolvedFee
            : resolvedFee || existing?.perSemFee || 0,
          materialFee: locked
            ? existing?.materialFee ?? term.materialFee ?? 0
            : term.materialFee ?? existing?.materialFee ?? 0,
          discount: locked
            ? existing?.discount ?? resolvedDiscount
            : resolvedDiscount || existing?.discount || 0,
          paidAmount: locked
            ? existing?.paidAmount ?? 0
            : existing?.paidAmount ?? term.paidAmount ?? 0,
          commission: existing?.commission ?? 0,
          bonus: existing?.bonus ?? 0,
          isCommissionClaimed: existing?.isCommissionClaimed ?? false,
          isBonusClaimed: existing?.isBonusClaimed ?? false,
        },
        new Date()
      );
    })
    .filter(Boolean);
};

export const buildAdmissionInstallmentsFromValues = (parentId, values, existingDetails = []) => {
  const count = Math.max(0, toNumber(values?.semester || existingDetails?.length || 0));
  return Array.from({ length: count }, (_, index) => {
    const existing = existingDetails[index] || {};
    return calculateSubRowValues(
      {
        ...createNewAdmissionDetail(parentId, index + 1),
        ...existing,
        parentId,
        index: index + 1,
        date: existing?.date || values?.startDate || format(new Date(), "yyyy-MM-dd"),
        terms: existing?.terms || String(index + 1),
        perSemFee: existing?.perSemFee ?? values?.perSemFee ?? 0,
        materialFee:
          existing?.materialFee ?? toNumber(values?.materialFee) + toNumber(values?.enrolmentFee),
        discount: existing?.discount ?? values?.discount ?? 0,
        commission: existing?.commission ?? values?.commission ?? 0,
        bonus: existing?.bonus ?? values?.bonus ?? 0,
        isCommissionClaimed: existing?.isCommissionClaimed ?? false,
        isBonusClaimed: existing?.isBonusClaimed ?? false,
      },
      new Date()
    );
  });
};

/**
 * What a term's commission/bonus is actually worth to the agency. A withdrawal writes off whatever
 * it did not explicitly retain, so every figure derived from it — profit/loss, agent cost, the
 * parent row's scalars — has to read it through here or it reinstates money that was given up.
 * A row with no withdrawalState is every admission that is not withdrawn: counted in full.
 */
export const countedCommission = (row = {}) =>
  row.withdrawalState == null || row.withdrawalCommissionRetained === true
    ? toNumber(row.commission)
    : 0;

export const countedBonus = (row = {}) =>
  row.withdrawalState == null || row.withdrawalBonusRetained === true ? toNumber(row.bonus) : 0;

/** Rounds to cents, which also turns -0 (rendered "-0.00") into 0. */
const cents = (value) => {
  const rounded = Math.round(value * 100) / 100;
  return rounded === 0 ? 0 : rounded;
};

const localDateKey = (value) => {
  if (value instanceof Date) return isNaN(value) ? null : toLocalISODate(value);
  const text = String(value).trim();
  const isoDay = /^\d{4}-\d{2}-\d{2}/.exec(text);
  if (isoDay) return isoDay[0];
  const parsed = new Date(text);
  return isNaN(parsed) ? null : toLocalISODate(parsed);
};

/**
 * Mirror of the server's termDatePassed: a term counts from its own date on, and an undated or
 * unreadable one counts now. Compared as calendar days, because `new Date("2026-09-16")` is UTC
 * midnight, which east of UTC is later than local midnight and pushed today's term to tomorrow.
 */
export const isTermDatePassed = (value, today = new Date()) => {
  if (!value) return true;
  const date = localDateKey(value);
  const cutoff = localDateKey(today);
  return !date || !cutoff || date <= cutoff;
};

/**
 * Whether a term belongs in the effective (as-of-today) figures. Mirrors the server: an imported
 * installment is reached on its date or now when it has none, a term not imported yet (see
 * UnimportedAdmissionTerm) only once its own date is set and reached.
 */
export const isAdmissionTermReached = (row, today = new Date()) => {
  if (!row?._unimported) return isTermDatePassed(row?.date || row?.admissionDate, today);
  const date = row.date ? localDateKey(row.date) : null;
  return Boolean(date) && isTermDatePassed(date, today);
};

const isTermCommissionClaimed = (row) =>
  Boolean(row.isCommissionClaimed) || isCommissionMoneyClaimed(row);
const isTermBonusClaimed = (row) => Boolean(row.isBonusClaimed) || isBonusMoneyClaimed(row);

/**
 * Mirror of the backend's AdmissionDetailFinancials — keep the two in step.
 *
 *   Agreed      = Fee + Extra − Discount
 *   Cost        = Fee + Extra − Commission (if claimed) − Bonus (if claimed)
 *   Due         = Agreed − Paid
 *   Profit/Loss = Agreed − Cost − Due − Referral      (0 until the term is reached)
 *
 * A term makes profit or loss only once reached (isAdmissionTermReached); before that it still shows
 * what it will cost and owe. Every claim counts, chip-only ones included.
 *
 * A SETTLEMENT term is priced at settledAgreed instead of fee + extra − discount. A CANCELLED term
 * agrees, costs and owes nothing, so its profit is whatever retained commission/bonus was claimed,
 * less referral. On either, commission/bonus count only when explicitly retained.
 */
const priceAdmissionTerm = (row, today) => {
  const claimed =
    (isTermCommissionClaimed(row) ? countedCommission(row) : 0) +
    (isTermBonusClaimed(row) ? countedBonus(row) : 0);
  const referral = toNumber(row.referral);
  const reached = isAdmissionTermReached(row, today);

  if (row.withdrawalState === "CANCELLED") {
    return { agreed: 0, cost: 0, due: 0, profitLoss: reached ? cents(claimed - referral) : 0 };
  }

  // Settling replaces the fee base outright; the settled figure is already net of discount.
  const settled = row.withdrawalState === "SETTLEMENT";
  const feeBase = settled
    ? toNumber(row.settledAgreed)
    : toNumber(row.perSemFee) + toNumber(row.materialFee);
  const agreed = cents(settled ? feeBase : feeBase - toNumber(row.discount));
  const cost = cents(feeBase - claimed);
  const due = cents(agreed - toNumber(row.paidAmount));
  return { agreed, cost, due, profitLoss: reached ? cents(agreed - cost - due - referral) : 0 };
};

/** What the term row shows, and what every total adds up. */
export const admissionTermRowFigures = (row = {}, today = new Date()) =>
  priceAdmissionTerm(row, today);

// Takes only the row: it is passed straight to Array.map, whose second argument is an index.
export const calculateSubRowValues = (subRow) => {
  const figures = admissionTermRowFigures(subRow);
  return {
    ...subRow,
    agreed: figures.agreed,
    perSemCost: figures.cost,
    dueAmount: figures.due,
    profitLoss: figures.profitLoss,
  };
};

const EMPTY_ADMISSION_TOTALS = {
  agreedAmount: 0,
  cost: 0,
  dueAmount: 0,
  effectiveDueAmount: 0,
  paidAmount: 0,
  referral: 0,
  profitLoss: 0,
  discount: 0,
};

/**
 * Totals terms the way the server's rollup does: through admissionTermRowFigures, netting a term
 * paid over its agreed amount against what the others owe. Due counts every term, reached or not,
 * which is what "Due" means everywhere else; `effectiveDueAmount` is the same sum restricted to
 * terms whose date has been reached, and is the only figure the date gates.
 */
const sumAdmissionTerms = (rows = [], today = new Date()) => {
  const sums = (rows || []).reduce((acc, item) => {
    const figures = admissionTermRowFigures(item, today);
    const owed = figures.due;
    return {
      agreedAmount: acc.agreedAmount + figures.agreed,
      cost: acc.cost + figures.cost,
      dueAmount: acc.dueAmount + owed,
      effectiveDueAmount: acc.effectiveDueAmount + (isAdmissionTermReached(item, today) ? owed : 0),
      paidAmount: acc.paidAmount + toNumber(item.paidAmount),
      referral: acc.referral + toNumber(item.referral),
      profitLoss: acc.profitLoss + figures.profitLoss,
      discount: acc.discount + toNumber(item.discount),
    };
  }, EMPTY_ADMISSION_TOTALS);
  return Object.fromEntries(Object.entries(sums).map(([key, value]) => [key, cents(value)]));
};

/**
 * The withdrawal columns a term carries and its installment mirror may not have yet — an
 * un-imported term has no installment at all, and an imported one is read from an account payload
 * that does not ship them. Display-side only: these are never written back.
 */
export const withdrawalFieldsFromTerm = (term = {}) => ({
  withdrawalState: term.withdrawalState ?? null,
  withdrawalSettlementMode: term.withdrawalSettlementMode ?? null,
  settledAgreed: term.settledAgreed ?? null,
  withdrawalCommissionRetained: term.withdrawalCommissionRetained ?? null,
  withdrawalBonusRetained: term.withdrawalBonusRetained ?? null,
});

/** The term's chip-only flags, shown as a label on the term. Display-side only. */
export const chipOnlyFieldsFromTerm = (term = {}, installment = null) => ({
  commissionChipOnly: Boolean(term.commissionChipOnly ?? installment?.commissionChipOnly),
  bonusChipOnly: Boolean(term.bonusChipOnly ?? installment?.bonusChipOnly),
});

// Calculates derived fields for a main table row, handling both Admission and non-Admission modules
export const calculateRowValues = (row) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize to start of day for accurate date comparison

  if (row.module === "Admission") {
    const updatedAdmissionDetails = (row.admissionDetails || []).map(calculateSubRowValues);

    // Parent Admission row totals sum every installment (matches expanded sub-table).
    const totals = sumAdmissionTerms(updatedAdmissionDetails, today);
    const claimState = deriveParentClaimState(updatedAdmissionDetails, today);

    return {
      ...row,
      admissionDetails: updatedAdmissionDetails,
      ...totals,
      commission: claimState.commission,
      bonus: claimState.bonus,
      isCommissionClaimed: claimState.isCommissionClaimed,
      isBonusClaimed: claimState.isBonusClaimed,
    };
  } else {
    // Ensure numeric values for non-Admission modules
    const paidAmount = toNumber(row.paidAmount);
    const cost = toNumber(row.cost);
    const referral = toNumber(row.referral);
    const agreedAmount = toNumber(row.agreedAmount);
    const agreed = toNumber(row.agreed); //mara cost
    const discount = toNumber(row.discount);
    const bonus = toNumber(row.bonus);
    const bonusAmount = row?.isBonusClaimed ? bonus : 0;

    let commission = toNumber(row.commission);
    let commissionAmount = row?.isCommissionClaimed ? commission : 0;
    let isCommissionClaimed = Boolean(row?.isCommissionClaimed);
    // Insurance commission is money already received, so it counts in full whatever the claim toggle
    // says: the server's monthly ledger when there is one, and otherwise the row's own scalar, which
    // is what the rows predating the ledger carry.
    if (row.module === "Insurance") {
      const months = Array.isArray(row.commissionMonths) ? row.commissionMonths : [];
      commission = months.length
        ? months.reduce((sum, m) => sum + toNumber(m.amount), 0)
        : toNumber(row.commission);
      commissionAmount = commission;
      isCommissionClaimed = commission > 0;
    }

    // Never below 0, matching ModulePaymentAccountBridgeService: a discount larger than the
    // outstanding balance used to leave the row owing a negative amount.
    const dueAmount = Math.max(0, agreedAmount - paidAmount - discount);
    const profitLoss = paidAmount - cost + commissionAmount + bonusAmount - referral - agreed;

    return {
      ...row,
      commission,
      isCommissionClaimed,
      profitLoss,
      dueAmount,
    };
  }
};

/**
 * Re-prices one staged (pre-import) admission term. Delegates to {@link calculateSubRowValues} so a
 * staged row is priced by exactly the rule the server will apply on import, and the figures a
 * reviewer approves are the figures that get written.
 *
 * Amounts come back as strings because that is what the account DTO carries end to end.
 */
export const recalculateAdmissionDetail = (detail = {}) => {
  const computed = calculateSubRowValues(detail);
  return {
    ...computed,
    agreed: String(computed.agreed),
    perSemCost: String(computed.perSemCost),
    dueAmount: String(computed.dueAmount),
    profitLoss: String(computed.profitLoss),
  };
};

/** Prices a whole staged account entry — an admission and its terms, or a single module row. */
export const buildVerificationSnapshot = (detail = {}) => {
  if (detail.module === "Admission") {
    const admissionDetails = (detail.admissionDetails || []).map(recalculateAdmissionDetail);
    const totals = sumAdmissionTerms(admissionDetails);
    return {
      ...detail,
      admissionDetails,
      ...Object.fromEntries(Object.entries(totals).map(([key, value]) => [key, String(value)])),
    };
  }

  const computed = calculateRowValues(detail);
  return {
    ...computed,
    dueAmount: String(computed.dueAmount),
    profitLoss: String(computed.profitLoss),
  };
};

const unimportedTermRow = (term) => ({
  _unimported: true,
  ...withdrawalFieldsFromTerm(term),
  ...chipOnlyFieldsFromTerm(term),
  terms: `T${term.termNumber}`,
  date: term.admissionDate || "",
  censusDate: term.censusDate ?? "",
  perSemFee: term.perSemFee ?? "",
  materialFee: term.materialFee ?? "",
  discount: term.discount ?? "",
  commission: term.commission ?? "",
  bonus: term.bonus ?? "",
  isCommissionClaimed: Boolean(term.isCommissionClaimed),
  isBonusClaimed: Boolean(term.isBonusClaimed),
  paidAmount: resolveTermPaidAmountFromPayments(term.payments, ""),
});

export const mergeAdmissionInstallmentsWithTerms = (admissionDetails = [], admissionTerms = []) => {
  if (!Array.isArray(admissionTerms) || admissionTerms.length === 0) {
    return (admissionDetails || []).map((detail) => calculateSubRowValues(detail));
  }
  const importedByTermNum = new Map();
  (admissionDetails || []).forEach((detail) => {
    const num = parseInstallmentTermNumber(detail.terms, null);
    if (num != null) importedByTermNum.set(num, detail);
  });
  return admissionTerms.map((term) => {
    const imported = importedByTermNum.get(term.termNumber);
    if (imported) {
      return calculateSubRowValues({
        ...imported,
        ...withdrawalFieldsFromTerm(term),
        ...chipOnlyFieldsFromTerm(term, imported),
        date: imported.date || imported.admissionDate || term.admissionDate || "",
        censusDate: imported.censusDate ?? term.censusDate ?? "",
      });
    }
    return calculateSubRowValues(unimportedTermRow(term));
  });
};

export const sumAdmissionInstallmentTotals = (installments = [], today = new Date()) =>
  sumAdmissionTerms(installments, today);

/**
 * Display totals for an admission row over every scheduled term, imported or not, as the server
 * counts them; the effective due keeps to reached terms. The schedule still lends each installment
 * its term's withdrawal and claim-mode fields.
 */
export const projectAdmissionRow = (row, terms, today = new Date()) => {
  if (row?.module !== "Admission" || !Array.isArray(terms) || terms.length === 0) return row;
  const scheduled = new Set(terms.map((term) => term.termNumber));
  const withoutTerm = (row.admissionDetails || []).filter(
    (detail) => !scheduled.has(parseInstallmentTermNumber(detail.terms, null))
  );
  const installments = [
    ...mergeAdmissionInstallmentsWithTerms(row.admissionDetails, terms),
    ...withoutTerm,
  ];
  const totals = sumAdmissionTerms(installments, today);
  return {
    ...row,
    agreedAmount: totals.agreedAmount,
    cost: totals.cost,
    discount: totals.discount,
    paidAmount: totals.paidAmount,
    dueAmount: totals.dueAmount,
    effectiveDueAmount: totals.effectiveDueAmount,
    referral: totals.referral,
    profitLoss: totals.profitLoss,
  };
};

/**
 * Projects every admission row of an account. An admission hidden from the list keeps only its own
 * (imported) figures, because the server adds no un-imported terms for it either.
 */
export const projectAccountDetails = (rows, termsOf, isVisible, today = new Date()) =>
  (rows || []).map((row) => (isVisible(row) ? projectAdmissionRow(row, termsOf(row), today) : row));

/**
 * The client tab's figures for one admission, matching the server's client summary: every term,
 * imported or not, priced from the term schedule and its live verified payments, netted across terms,
 * and floored at 0 for the admission as a whole. The effective due keeps to reached terms.
 */
export const admissionSnapshotTotals = (terms = [], installments = [], today = new Date()) => {
  const rows =
    Array.isArray(terms) && terms.length > 0
      ? terms.flatMap((term) => {
          const imported = (installments || []).find(
            (detail) => parseInstallmentTermNumber(detail.terms, null) === term.termNumber
          );
          if (!imported) return [unimportedTermRow(term)];
          return [
            {
              ...imported,
              ...withdrawalFieldsFromTerm(term),
              perSemFee: term.perSemFee,
              materialFee: term.materialFee,
              discount: term.discount,
              paidAmount: resolveTermPaidAmountFromPayments(term.payments, imported.paidAmount),
              date: imported.date || imported.admissionDate || term.admissionDate,
            },
          ];
        })
      : installments || [];
  const totals = sumAdmissionTerms(rows, today);
  return {
    totalAgreed: totals.agreedAmount,
    totalPaid: totals.paidAmount,
    totalDue: Math.max(0, totals.dueAmount),
    totalEffectiveDue: Math.max(0, totals.effectiveDueAmount),
    totalDiscount: totals.discount,
    totalReferral: totals.referral,
  };
};

/**
 * What an entry owes as of today. Only admission terms carry dates, so every other entry owes its
 * whole due. Terms net against each other, but an entry in credit overall owes 0, never less.
 */
export const entryEffectiveDue = (item, today = new Date()) => {
  if (item?.effectiveDueAmount != null) return Math.max(0, toNumber(item.effectiveDueAmount));
  if (item?.module === "Admission" && Array.isArray(item.admissionDetails)) {
    return Math.max(0, sumAdmissionTerms(item.admissionDetails, today).effectiveDueAmount);
  }
  return Math.max(0, toNumber(item?.dueAmount));
};

export const calculateTotals = (accountDetails, today = new Date()) => {
  return accountDetails.reduce(
    (totals, item) => ({
      totalPaid: totals.totalPaid + toNumber(item.paidAmount),
      totalCost: totals.totalCost + toNumber(item.cost),
      totalDue: totals.totalDue + Math.max(0, toNumber(item.dueAmount)),
      totalEffectiveDue: totals.totalEffectiveDue + entryEffectiveDue(item, today),
      totalProfitLoss: totals.totalProfitLoss + toNumber(item.profitLoss),
      totalCommission: totals.totalCommission + toNumber(item.commission),
    }),
    {
      totalPaid: 0,
      totalCost: 0,
      totalDue: 0,
      totalEffectiveDue: 0,
      totalProfitLoss: 0,
      totalCommission: 0,
    }
  );
};

// export const calculateRowValues = (row) => {
//   const today = new Date();
//   today.setHours(0, 0, 0, 0); // Normalize to start of day

//   if (row.module === "Admission") {
//     const validAdmissionDetails = row.admissionDetails.filter((subRow) => {
//       if (!subRow.date) return true; // Include if date is empty/undefined
//       const rowDate = new Date(subRow.date);
//       return rowDate < today;
//     });
//     const admissionDetails = row.admissionDetails.map((subRow) => {
//       const agreed =
//         Number(subRow.perSemFee || 0) +
//         Number(subRow.materialFee || 0) -
//         Number(subRow.discount || 0);
//       const cost =
//         Number(subRow.perSemFee || 0) +
//         Number(subRow.materialFee || 0) -
//         (subRow.isCommissionClaimed ? Number(subRow.commission || 0) : 0) -
//         (subRow.isBonusClaimed ? Number(subRow.bonus || 0) : 0);
//       const due =
//         Number(subRow.perSemFee || 0) +
//         Number(subRow.materialFee || 0) -
//         Number(subRow.discount || 0) -
//         Number(subRow.paidAmount || 0);
//       const profitLoss = agreed - cost - due - Number(subRow.referral || 0);

//       return {
//         ...subRow,
//         perSemCost: cost,
//         dueAmount: due,
//         profitLoss,
//         perSemAgreed: agreed,
//       };
//       //   }

//       //   return subRow;
//     });

//     const agreedAmount = validAdmissionDetails.reduce(
//       (sum, item) =>
//         sum + Number(item.perSemFee || 0) - Number(item.discount || 0),
//       0
//     );
//     const cost = validAdmissionDetails.reduce(
//       (sum, item) => sum + Number(item.perSemCost || 0),
//       0
//     );
//     const dueAmount = validAdmissionDetails.reduce(
//       (sum, item) => sum + Number(item.dueAmount || 0),
//       0
//     );
//     const paidAmount = validAdmissionDetails.reduce(
//       (sum, item) => sum + Number(item.paidAmount || 0),
//       0
//     );
//     const referral = validAdmissionDetails.reduce(
//       (sum, item) => sum + Number(item.referral || 0),
//       0
//     );
//     const profitLoss = validAdmissionDetails.reduce(
//       (sum, item) => sum + Number(item.profitLoss || 0),
//       0
//     );
//     const discount = validAdmissionDetails.reduce(
//       (sum, item) => sum + Number(item.discount || 0),
//       0
//     );
//     const commission = validAdmissionDetails.reduce(
//       (sum, item) =>
//         sum + item.isCommissionClaimed ? Number(item.commission || 0) : 0,
//       0
//     );
//     const bonus = validAdmissionDetails.reduce(
//       (sum, item) => (sum + item.isBonusClaimed ? Number(item.bonus || 0) : 0),
//       0
//     );

//     return {
//       ...row,
//       admissionDetails,
//       agreedAmount,
//       cost,
//       dueAmount,
//       paidAmount,
//       referral,
//       profitLoss,
//       discount,
//       commission,
//       bonus,
//     };
//   } else {
//     const profitLoss =
//       Number(row.paidAmount || 0) -
//       Number(row.cost || 0) -
//       Number(row.referral || 0);
//     const dueAmount =
//       Number(row.agreedAmount || 0) - Number(row.paidAmount || 0);

//     return {
//       ...row,
//       profitLoss,
//       dueAmount,
//     };
//   }
// };
